import React, { useState } from 'react';
import { 
  Box, 
  Group, 
  Text, 
  Stack,
  Chip,
} from '@mantine/core';
import { ProjectExperience, ExternalRole, ProjectRole } from '../../../../../types/cv';
import { DeleteConfirmationModal } from '../../../../../components/common/DeleteConfirmationModal';
import { useEditMode } from "../../../../../context/EditModeContext";
import { CVSectionContainer } from '../../../../../components/common/CVSectionContainer';

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">Roles you have had in the project and corresponding role description:</Text>
    <Text size="sm" ml={10}>• Your role in the project along with a description of your areas of responsibility and a description of your tasks. If you have had multiple roles, you divide them up and create a new role, where you also fill in the role description.</Text>
  </Stack>
);

interface RoleOverviewProps {
  projectExperience: ProjectExperience[];
  externalRoles: ExternalRole[];
  onExternalRolesChange: (roles: ExternalRole[]) => void;
}

export function RoleOverview({ projectExperience, externalRoles, onExternalRolesChange }: RoleOverviewProps) {
  const { isEditMode } = useEditMode();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<ExternalRole | null>(null);

  // Get unique roles from project experience
  const getUniqueRoles = (): ProjectRole[] => {
    const uniqueRoles = new Map<string, ProjectRole>();
    projectExperience.forEach(project => {
      if (project.roles && Array.isArray(project.roles)) {
        project.roles.forEach(role => {
          if (role && role.name && !uniqueRoles.has(role.name)) {
            uniqueRoles.set(role.name, role);
          }
        });
      }
    });
    return Array.from(uniqueRoles.values());
  };

  const openDeleteConfirmation = (role: ExternalRole) => {
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (roleToDelete) {
      onExternalRolesChange(externalRoles.filter(r => r.id !== roleToDelete.id));
    }
  };

  const uniqueRoles = getUniqueRoles();

  return (
    <CVSectionContainer 
      sectionId="roles-overview"
      title="Roles Overview"
      instructions={isEditMode ? INSTRUCTIONS : undefined}
    >
      <Stack gap="md">
        <Box>
          <Text fw={500} size="sm" mb="xs">Unique roles from project experience</Text>
          <Group gap="xs">
            {uniqueRoles.map((role, index) => (
              <Chip
                key={index}
                variant="filled"
                size="sm"
                radius="sm"
              >
                {role.name}
              </Chip>
            ))}
          </Group>
        </Box>

        {externalRoles.length > 0 && (
          <Box>
            <Text fw={500} size="sm" mb="xs">External roles</Text>
            <Group gap="xs">
              {externalRoles.map((role) => (
                <Chip
                  key={role.id}
                  variant="filled"
                  size="sm"
                  radius="sm"
                  onClick={isEditMode ? () => {
                    onExternalRolesChange(
                      externalRoles.map(r => 
                        r.id === role.id ? { ...r, includeInCV: !r.includeInCV } : r
                      )
                    );
                  } : undefined}
                  color={role.includeInCV ? 'brand' : 'gray'}
                >
                  {role.name}
                </Chip>
              ))}
            </Group>
          </Box>
        )}
      </Stack>

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={roleToDelete ? `role "${roleToDelete.name}"` : undefined}
      />
    </CVSectionContainer>
  );
}

